/**
 * Feature extraction entry points.
 *
 * Thin TypeScript surface over the Rust/WASM feature detectors. The previous
 * pure-TS implementation lived here and has been replaced so every caller
 * (production, debug views, tests) goes through the same WASM kernel.
 */

import {
  computeDeepShadowMask,
  extractCreases as wasmExtractCreases,
  extractScreenEdges as wasmExtractScreenEdges,
  extractSilhouettes as wasmExtractSilhouettes,
  thinFeatureRidges as wasmThinFeatureRidges,
} from "../../../wasm/inking.ts";
import type { FeatureMap, GBufferData, InkingPipelineParams } from "./types.ts";

/**
 * Thin broad crease / screen-edge bands to single-pixel ridges via directional
 * non-maximum suppression. Silhouette pixels pass through unchanged.
 */
export function thinFeatureRidges(
  featureMap: FeatureMap,
  minConfidence: number = 0.15,
): FeatureMap {
  return wasmThinFeatureRidges(featureMap, minConfidence);
}

/**
 * Run all enabled feature detectors and return a fused feature map.
 *
 * Detectors are run lowest-priority first so higher-priority detections
 * overwrite the same pixel (silhouette > crease > screen edge).
 */
export function extractFeatures(
  gbuffer: GBufferData,
  params: InkingPipelineParams,
): FeatureMap {
  const { width, height } = gbuffer;
  const pixelCount = width * height;

  const out: FeatureMap = {
    width,
    height,
    confidence: new Float32Array(pixelCount),
    featureType: new Uint8Array(pixelCount),
    silhouetteObjectId: new Uint32Array(pixelCount),
    silhouetteNeighborId: new Uint32Array(pixelCount),
    silhouetteKind: new Uint8Array(pixelCount),
    resolvedAutoSmoothAngles: new Float32Array(
      gbuffer.resolvedAutoSmoothAngles,
    ),
  };

  const deepShadowMask = computeDeepShadowMask(gbuffer, params);

  if (params.features.screenEdges) {
    wasmExtractScreenEdges(
      gbuffer,
      params.screenEdgeDetail,
      deepShadowMask,
      out,
    );
  }
  if (params.features.crease) {
    wasmExtractCreases(gbuffer, params.creaseAngle, out);
  }
  if (params.features.silhouette) {
    wasmExtractSilhouettes(gbuffer, out);
  }

  return out;
}
